// src/modules/competitions/services/imageTypesService.ts

import { httpClient } from '../../../shared/services/httpClient';
import { API_ENDPOINTS } from '../../../shared/services/apiConfig';
import {
  ImageTypesApiResponse,
  CriteriaApiResponse,
  ExampleImagesApiResponse,
  DeleteSubmissionApiResponse,
  CompetitionImageType,
} from '../types';

class ImageTypesService {
  /**
   * Obtener tipos de imagen por categoría y nivel
   */
  async getImageTypesByCategory(
    categoryId: number,
    level: string
  ): Promise<ImageTypesApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_IMAGE_TYPES, {
      category_id: categoryId,
      level: level,
    });
  }

  /**
   * Obtener criterios de evaluación por tipo de imagen
   */
  async getCriteriaByImageType(
    imageTypeId: number
  ): Promise<CriteriaApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_CRITERIA, {
      image_type_id: imageTypeId,
    });
  }

  /**
   * ✅ NUEVO: Obtener imágenes ejemplo por tipo de imagen
   */
  async getExampleImages(imageTypeId: number): Promise<ExampleImagesApiResponse> {
    return httpClient.get<any>(API_ENDPOINTS.COMPETITIONS.GET_EXAMPLE_IMAGES, {
      image_type_id: imageTypeId,
    });
  }

  /**
   * ✅ NUEVO: Subir imagen de participante
   */
  async submitImage(formData: FormData): Promise<any> {
    return httpClient.postFormData<any>(
      API_ENDPOINTS.COMPETITIONS.SUBMIT_IMAGE,
      formData
    );
  }

  /**
   * ✅ NUEVO: Eliminar imagen subida
   */
  async deleteSubmission(
    submissionId: number,
    participantId: number
  ): Promise<DeleteSubmissionApiResponse> {
    return httpClient.delete<null>(API_ENDPOINTS.COMPETITIONS.DELETE_SUBMISSION, {
      submission_id: submissionId,
      participant_id: participantId,
    });
  }

  /**
   * Validar si un archivo cumple con los requisitos
   */
  validateImageFile(
    file: File,
    imageType: CompetitionImageType
  ): { isValid: boolean; error?: string } {
    const maxSizeBytes = imageType.max_file_size_mb * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return {
        isValid: false,
        error: `El archivo es muy grande. Tamaño máximo: ${imageType.max_file_size_mb}MB`,
      };
    }

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !imageType.allowed_formats.includes(fileExtension)) {
      return {
        isValid: false,
        error: `Formato no permitido. Formatos aceptados: ${imageType.allowed_formats.join(', ')}`,
      };
    }

    return { isValid: true };
  }

  /**
   * Formatear tamaño de archivo
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}

export const imageTypesService = new ImageTypesService();